import type { TAsyncState, TStateCreator } from "../types";
import {
    initialAsyncState,
    pendingAsyncState,
    fulfilledAsyncState,
    rejectedAsyncState,
} from "../types";

/**
 * File status enum matching backend FileStatus (14 modern members).
 */
export type TFileStatus =
    | "not_received"
    | "uploaded"
    | "under_acceptance"
    | "under_review"
    | "ready_for_balancing"
    | "balanced"
    | "ready_for_commitment"
    | "committed"
    | "adjusted"
    | "finjournal_generated"
    | "data_platform_generated"
    | "completed"
    | "failed"
    | "rejected";

/**
 * File type enum
 */
export type TFileType = "tb" | "gl" | "otb" | "fin_journals" | "tb_gl";

/**
 * Why a file is in the archive, matching backend FileArchiveReason.
 *
 * `retention_period` is one-way. `deal_inactive` is reversed when the deal is
 * reactivated — except for files that aged past the window meanwhile, which stay
 * archived and keep this reason.
 */
export type TFileArchiveReason = "retention_period" | "deal_inactive";

/**
 * Financial file entity
 */
export type TFinancialFile = {
    id: string;
    dealId: string;
    dealName: string;
    serviceProviderName: string;
    bookId: string | null;
    bookName: string | null;
    period: string;
    fileType: TFileType;
    status: TFileStatus;
    originalFilename: string;
    uploadedById: string;
    uploadedByName: string;
    uploadedAt: string;
    processedAt: string | null;
    lineItemCount: number;
    totalAmount: number;
    validationErrors: string[];
    rejectionReason: string | null;
    yardiUploadStatus: string | null;
    yardiBatchId: string | null;
    /** When the file was archived; null while it is live. Shown on the Archive page. */
    archivedAt: string | null;
    /** Why it was archived. Non-null exactly when `archivedAt` is. */
    archiveReason: TFileArchiveReason | null;
    /** Whether the file's deal is active; null when the file has no deal. */
    dealIsActive: boolean | null;
};

/**
 * File line item entity
 */
export type TFileLineItem = {
    id: string;
    fileId: string;
    rowNumber: number;
    amount: number;
    sourceAccountNumber: string;
    /**
     * Second source-account component (multi-criteria CoA mapping). Null on
     * single-key deals and on every TB/OTB line.
     */
    sourceAccountNumber2: string | null;
    sourceAccountName: string | null;
    breAccountNumber: string | null;
    breAccountName: string | null;
    propcode: string | null;
    bookNumber: number;
    segment1: string | null;
    segment2: string | null;
    segment3: string | null;
    segment4: string | null;
    segment5: string | null;
    segment6: string | null;
    segment7: string | null;
    isValid: boolean;
    validationErrors: string[];
    isGenerated: boolean;
    canBeDeleted: boolean;
};

/**
 * File list filters
 */
export type TFileFilters = {
    dealId?: string;
    status?: TFileStatus;
    fileType?: TFileType;
    period?: string;
    page?: number;
    pageSize?: number;
};

/**
 * Paginated response type
 */
export type TPaginatedFiles = {
    items: TFinancialFile[];
    total: number;
    totalUnfiltered?: number;
    page: number;
    pageSize: number;
};

/**
 * Files slice state
 */
export type TFilesState = {
    filesListState: TAsyncState<TPaginatedFiles>;
    archivedFilesListState: TAsyncState<TPaginatedFiles>;
    currentFileState: TAsyncState<TFinancialFile>;
    lineItemsState: TAsyncState<TFileLineItem[]>;
    uploadState: TAsyncState<TFinancialFile>;
    fileFilters: TFileFilters;
};

/**
 * Files slice actions
 */
export type TFilesActions = {
    // List operations
    setFilesLoading: () => void;
    setFilesList: (data: TPaginatedFiles) => void;
    setFilesError: (error: Error) => void;
    setFileFilters: (filters: Partial<TFileFilters>) => void;
    resetFileFilters: () => void;

    // Archive list operations
    setArchivedFilesLoading: () => void;
    setArchivedFilesList: (data: TPaginatedFiles) => void;
    setArchivedFilesError: (error: Error) => void;

    // Current file operations
    setCurrentFileLoading: () => void;
    setCurrentFile: (file: TFinancialFile) => void;
    setCurrentFileError: (error: Error) => void;
    clearCurrentFile: () => void;
    updateCurrentFileStatus: (status: TFileStatus) => void;

    // Line items operations
    setLineItemsLoading: () => void;
    setLineItems: (items: TFileLineItem[]) => void;
    setLineItemsError: (error: Error) => void;
    updateLineItem: (itemId: string, updates: Partial<TFileLineItem>) => void;

    // Upload operations
    setUploadLoading: () => void;
    setUploadSuccess: (file: TFinancialFile) => void;
    setUploadError: (error: Error) => void;
    resetUpload: () => void;
};

/**
 * Complete files slice type
 */
export type TSliceFiles = TFilesState & TFilesActions;

const defaultFilters: TFileFilters = {
    page: 1,
    pageSize: 20,
};

const initialState: TFilesState = {
    filesListState: initialAsyncState(),
    archivedFilesListState: initialAsyncState(),
    currentFileState: initialAsyncState(),
    lineItemsState: initialAsyncState(),
    uploadState: initialAsyncState(),
    fileFilters: defaultFilters,
};

/**
 * Creates the files slice for the store.
 * Handles financial file state and operations.
 */
export const createSliceFiles = (): TStateCreator<TSliceFiles> => {
    return (set) => ({
        ...initialState,

        // List operations
        setFilesLoading: () => {
            set((state) => ({
                ...state,
                filesListState: pendingAsyncState(state.filesListState.data),
            }));
        },

        setFilesList: (data: TPaginatedFiles) => {
            set({ filesListState: fulfilledAsyncState(data) });
        },

        setFilesError: (error: Error) => {
            set((state) => ({
                ...state,
                filesListState: rejectedAsyncState(error, state.filesListState.data),
            }));
        },

        setFileFilters: (filters: Partial<TFileFilters>) => {
            set((state) => ({
                ...state,
                fileFilters: { ...state.fileFilters, ...filters },
            }));
        },

        resetFileFilters: () => {
            set({ fileFilters: defaultFilters });
        },

        // Archive list operations
        setArchivedFilesLoading: () => {
            set((state) => ({
                ...state,
                archivedFilesListState: pendingAsyncState(state.archivedFilesListState.data),
            }));
        },

        setArchivedFilesList: (data: TPaginatedFiles) => {
            set({ archivedFilesListState: fulfilledAsyncState(data) });
        },

        setArchivedFilesError: (error: Error) => {
            set((state) => ({
                ...state,
                archivedFilesListState: rejectedAsyncState(
                    error,
                    state.archivedFilesListState.data,
                ),
            }));
        },

        // Current file operations
        setCurrentFileLoading: () => {
            set((state) => ({
                ...state,
                currentFileState: pendingAsyncState(state.currentFileState.data),
            }));
        },

        setCurrentFile: (file: TFinancialFile) => {
            set({ currentFileState: fulfilledAsyncState(file) });
        },

        setCurrentFileError: (error: Error) => {
            set({ currentFileState: rejectedAsyncState(error) });
        },

        clearCurrentFile: () => {
            set({
                currentFileState: initialAsyncState(),
                lineItemsState: initialAsyncState(),
            });
        },

        updateCurrentFileStatus: (status: TFileStatus) => {
            set((state) => {
                if (state.currentFileState.data) {
                    return {
                        ...state,
                        currentFileState: fulfilledAsyncState({
                            ...state.currentFileState.data,
                            status,
                        }),
                    };
                }
                return state;
            });
        },

        // Line items operations
        setLineItemsLoading: () => {
            set((state) => ({
                ...state,
                lineItemsState: pendingAsyncState(state.lineItemsState.data),
            }));
        },

        setLineItems: (items: TFileLineItem[]) => {
            set({ lineItemsState: fulfilledAsyncState(items) });
        },

        setLineItemsError: (error: Error) => {
            set({ lineItemsState: rejectedAsyncState(error) });
        },

        updateLineItem: (itemId: string, updates: Partial<TFileLineItem>) => {
            set((state) => {
                if (state.lineItemsState.data) {
                    const updatedItems = state.lineItemsState.data.map((item) =>
                        item.id === itemId ? { ...item, ...updates } : item,
                    );
                    return {
                        ...state,
                        lineItemsState: fulfilledAsyncState(updatedItems),
                    };
                }
                return state;
            });
        },

        // Upload operations
        setUploadLoading: () => {
            set({ uploadState: pendingAsyncState() });
        },

        setUploadSuccess: (file: TFinancialFile) => {
            set({ uploadState: fulfilledAsyncState(file) });
        },

        setUploadError: (error: Error) => {
            set({ uploadState: rejectedAsyncState(error) });
        },

        resetUpload: () => {
            set({ uploadState: initialAsyncState() });
        },
    });
};
